type RequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  timeoutMs?: number;
  path?: string;
};

const BASE_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL) || "";
let authToken: string | null = null;
let on401Callback: (() => void) | null = null;
let isLoggingOut = false; // Prevent multiple logout calls
let pendingRequests: Array<{
  controller: AbortController;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}> = [];

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function set401Callback(callback: () => void) {
  on401Callback = callback;
}

export function clear401Callback() {
  on401Callback = null;
}

// Enhanced error types for better error handling
export class PayGateApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public detail?: any,
    public isNetwork: boolean = false,
    public isTimeout: boolean = false
  ) {
    super(message);
    this.name = 'PayGateApiError';
  }
}

export async function request<T>(pathOrUrl: string, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 15000);

  // Add to pending requests if 401 callback is set
  if (on401Callback && !isLoggingOut) {
    pendingRequests.push({ controller, resolve: () => {}, reject: () => {} });
  }

  try {
    const isAbsolute = /^https?:\/\//.test(pathOrUrl);
    const url = isAbsolute ? pathOrUrl : `${BASE_URL}${pathOrUrl}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

    const res = await fetch(url, {
      method: options.method || "GET",
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: "include",
      signal: controller.signal,
    });

    if (!res.ok) {
      const status = res.status;
      let detail = undefined;
      let errorMessage = '';

      // Handle 401 Unauthorized - trigger auto-logout
      if (status === 401 && on401Callback && !isLoggingOut) {
        isLoggingOut = true; // Prevent multiple logout calls

        // Abort all pending requests
        pendingRequests.forEach(req => {
          req.controller.abort();
        });
        pendingRequests = [];

        // Execute logout callback asynchronously to avoid blocking
        setTimeout(() => {
          try {
            on401Callback();
          } catch (error) {
            console.error('401 callback error:', error);
          } finally {
            isLoggingOut = false;
          }
        }, 100);
      }

      // Properly parse error response with better error handling
      try {
        const errorData = await res.json();
        detail = errorData;
        errorMessage = errorData.message || errorData.error || '';
      } catch (parseError) {
        // If parsing fails, use status-based error messages
        errorMessage = getStatusErrorMessage(status);
      }

      // Create user-friendly error message
      const userMessage = getUserFriendlyErrorMessage(status, errorMessage, pathOrUrl);
      const error = new PayGateApiError(
        userMessage,
        status,
        detail,
        false,
        false
      );

      throw error;
    }

    // Parse successful response with proper error handling
    try {
      const json = (await res.json()) as T;
      return json;
    } catch (parseError) {
      throw new PayGateApiError(
        'Invalid response format from PayGate server',
        res.status,
        undefined,
        false,
        false
      );
    }
  } catch (error) {
    // Handle network and timeout errors
    if (error instanceof PayGateApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new PayGateApiError(
          'Request timed out. Please check your connection and try again.',
          undefined,
          undefined,
          false,
          true
        );
      }

      if (error.message.includes('fetch')) {
        throw new PayGateApiError(
          'Network connection failed. Please check your internet connection.',
          undefined,
          undefined,
          true,
          false
        );
      }

      throw new PayGateApiError(
        'An unexpected error occurred. Please try again.',
        undefined,
        { originalError: error.message },
        false,
        false
      );
    }

    throw error;
  } finally {
    clearTimeout(timeout);

    // Remove from pending requests on completion
    pendingRequests = pendingRequests.filter(req => req.controller !== controller);
  }
}

// Helper functions for better error messages
function getStatusErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request data';
    case 401:
      return 'Authentication required';
    case 403:
      return 'Access denied';
    case 404:
      return 'Resource not found';
    case 429:
      return 'Too many requests - please wait';
    case 500:
      return 'Server error - please try again later';
    case 502:
      return 'Service temporarily unavailable';
    case 503:
      return 'Service maintenance in progress';
    default:
      return `Request failed (${status})`;
  }
}

function getUserFriendlyErrorMessage(status: number, serverMessage: string, path: string): string {
  // If server provides a good message, use it
  if (serverMessage && !serverMessage.includes('Error') && !serverMessage.includes('Exception')) {
    return serverMessage;
  }

  // Provide context-aware messages based on endpoint
  if (path.includes('/auth/')) {
    if (status === 401) return 'Invalid email or password';
    if (status === 403) return 'Account access denied';
    if (status === 429) return 'Too many login attempts - please wait';
  }

  if (path.includes('/transactions/')) {
    if (status === 404) return 'Transaction not found';
    if (status === 400) return 'Invalid transaction data';
  }

  if (path.includes('/payin') || path.includes('/payout')) {
    if (status === 400) return 'Invalid payment details';
    if (status === 403) return 'Insufficient permissions';
  }

  // Fallback to status-based messages
  return getStatusErrorMessage(status);
}
